<template>
  <div class="acSearchInput">
    <input
      ref="inputRef"
      :value="model"
      data-test="search-input-field"
      class="acSearchInput_field"
      type="text"
      :maxlength="MAX_LENGTH"
      :placeholder="$t('search.placeholder')"
      @input="onInput"
      @paste="onPaste"
      @keyup.enter="onSubmit"
    >

    <!-- Лупа кликабельна: тап по ней сабмитит запрос ровно так же, как Enter.
         mousedown.prevent — чтобы поле не теряло фокус: иначе на мобильных
         blur успевает закрыть оверлей раньше, чем сработает click, и сабмит
         теряется. При видимом крестике очистки уступает ему место справа. -->
    <button
      v-if="!isClearVisible"
      class="acSearchInput_magnifier"
      data-test="search-input-magnifier"
      type="button"
      :aria-label="$t('search.submit')"
      @mousedown.prevent
      @click="onSubmit"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.91365 6.1647C9.91365 6.72268 9.80375 7.2752 9.59022 7.79071C9.37669 8.30621 9.06371 8.77461 8.66916 9.16916C8.27461 9.56371 7.80621 9.87669 7.29071 10.0902C6.7752 10.3037 6.22268 10.4137 5.6647 10.4137C5.10672 10.4137 4.55421 10.3037 4.0387 10.0902C3.5232 9.87669 3.0548 9.56371 2.66025 9.16916C2.26569 8.77461 1.95272 8.30621 1.73919 7.79071C1.52566 7.2752 1.41576 6.72268 1.41576 6.1647C1.41576 5.03781 1.86341 3.95708 2.66025 3.16025C3.45708 2.36341 4.53781 1.91576 5.6647 1.91576C6.79159 1.91576 7.87233 2.36341 8.66916 3.16025C9.46599 3.95708 9.91365 5.03781 9.91365 6.1647ZM9.1394 10.6403C8.00104 11.524 6.56866 11.9407 5.13387 11.8056C3.69908 11.6704 2.36974 10.9935 1.41647 9.91265C0.463195 8.83183 -0.0423385 7.42834 0.00277973 5.9879C0.047898 4.54746 0.640276 3.17836 1.65932 2.15932C2.67836 1.14028 4.04746 0.547898 5.4879 0.50278C6.92834 0.457661 8.33183 0.963195 9.41265 1.91647C10.4935 2.86974 11.1704 4.19908 11.3056 5.63387C11.4407 7.06866 11.024 8.50104 10.1403 9.6394L12.7746 12.2737C12.8442 12.3386 12.9 12.4168 12.9387 12.5036C12.9774 12.5905 12.9982 12.6843 12.9999 12.7793C13.0016 12.8744 12.9841 12.9689 12.9485 13.0571C12.9128 13.1452 12.8598 13.2253 12.7926 13.2926C12.7253 13.3598 12.6452 13.4128 12.5571 13.4485C12.4689 13.4841 12.3744 13.5016 12.2793 13.4999C12.1843 13.4982 12.0905 13.4774 12.0036 13.4387C11.9168 13.4 11.8386 13.3442 11.7737 13.2746L9.1394 10.6403Z" fill="currentColor"/>
      </svg>
    </button>

    <button
      v-if="isClearVisible"
      data-test="search-input-clear"
      class="acSearchInput_clear"
      type="button"
      :aria-label="$t('search.clear')"
      @click="onClear"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 5L5 19M5 5L19 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, nextTick } from 'vue'

const MAX_LENGTH = 100
const FORBIDDEN = /[^a-zA-Zа-яёА-ЯЁ0-9 \-'?!.,]/g

const props = defineProps<{
  autofocus?: boolean
  // Крестик быстрой очистки — только в мобильном оверлее; в десктопной
  // шапке его нет по дизайну.
  clearable?: boolean
}>()
const model = defineModel<string>({ default: '' })
const emit = defineEmits<{ submit: [query: string] }>()

const inputRef = ref<HTMLInputElement | null>(null)

const isClearVisible = computed(() => Boolean(props.clearable && model.value && model.value.length > 0))

function sanitize (value: string): string {
  return value.replace(FORBIDDEN, '').slice(0, MAX_LENGTH)
}

async function onInput (event: Event): Promise<void> {
  const target = event.target as HTMLInputElement
  const raw = target.value
  const clean = sanitize(raw)

  if (clean === raw) {
    model.value = clean
    return
  }

  // Сохраняем каретку: считаем, сколько символов вырезано до неё.
  const caret = target.selectionStart ?? clean.length
  const removedBefore = raw.slice(0, caret).length - sanitize(raw.slice(0, caret)).length

  model.value = clean
  await nextTick()
  target.value = clean
  const nextCaret = Math.max(0, caret - removedBefore)
  target.setSelectionRange(nextCaret, nextCaret)
}

async function onPaste (event: ClipboardEvent): Promise<void> {
  event.preventDefault()
  const target = event.target as HTMLInputElement
  const pasted = event.clipboardData?.getData('text') ?? ''
  const cleanPasted = pasted.replace(FORBIDDEN, '')

  const start = target.selectionStart ?? 0
  const end = target.selectionEnd ?? 0
  const current = model.value ?? ''
  const merged = (current.slice(0, start) + cleanPasted + current.slice(end)).slice(0, MAX_LENGTH)

  model.value = merged
  await nextTick()
  const nextCaret = Math.min(start + cleanPasted.length, MAX_LENGTH)
  target.setSelectionRange(nextCaret, nextCaret)
}

// Общий обработчик сабмита: и keyup.enter в поле, и клик по лупе.
function onSubmit (): void {
  // Читаем значение из DOM, а не из model: `model` — это проп от родителя,
  // он обновляется асинхронно (emit → родитель пишет в свой ref → проп
  // возвращается сюда). При быстром наборе «текст + сразу Enter» (а также
  // при автозаполнении и вставке с последующим Enter) keyup прилетает
  // раньше, чем проп успел обновиться, и model.value ещё пуст — сабмит
  // молча не срабатывал. В DOM значение уже актуально к моменту keyup.
  // Без трима: строка из пробелов считается непустой (требование спеки).
  const value = inputRef.value?.value ?? model.value ?? ''
  if (value.length < 1) return
  // Синхронизируем модель на случай, если родитель ещё не догнал: иначе
  // он получит submit со значением, которого не видел.
  if (value !== model.value) model.value = value
  emit('submit', value)
}

function onClear (): void {
  model.value = ''
  // Фокус остаётся в поле — пользователь сразу печатает новый запрос.
  inputRef.value?.focus()
}

onMounted(() => {
  if (props.autofocus) inputRef.value?.focus()
})
</script>

<style lang="scss" scoped>
@import '@web/assets/scss/textContents.scss';

.acSearchInput {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  &_field {
    @extend .text-sm;

    // Миксин текстовых стилей ставит user-select: none !important — для поля
    // ввода это недопустимо, текст должно быть можно выделить и скопировать.
    user-select: text !important;
    width: 100%;
    height: 45px;
    box-sizing: border-box;
    // Справа — место под лупу/крестик, чтобы текст не заезжал под иконку.
    padding: 12px 44px 12px 14px;
    border: 1px solid var(--ac-color-line);
    border-radius: 42px;
    outline: none;
    transition: border-color 0.15s ease;
    background-color: #F4F5F7;
    color: var(--ac-color-dark-gray);
    caret-color: var(--ac-color-deep-liliac);

    &::placeholder {
      color: var(--ac-color-dark-gray);
    }

    // iOS Safari зумит страницу при фокусе на поле со шрифтом мельче 16px,
    // и зум не откатывается после ухода со страницы. 16px на мобильных
    // предотвращает зум (проектный приём — см. UpdateProfileStep).
    // !important — потому что .text-sm выше тоже ставит размер с !important.
    @media (max-width: 764px) {
      font-size: 16px !important;
    }

    &:focus {
      border-color: var(--ac-color-deep-liliac);
      background-color: var(--ac-color-white);
      color: var(--ac-color-deep-navy-blue);
    }
  }

  &_magnifier {
    position: absolute;
    // Иконка 14x14 — слишком мелкая цель для пальца, поэтому саму кнопку
    // растягиваем до 44px по ширине (минимум для тач-таргета). По высоте —
    // 100% поля: в десктопной шапке поле ниже 44px, и фиксированная высота
    // вылезала бы за его границы. Вид иконки при этом не меняется.
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 44px;
    padding: 0;
    // Кнопка, а не span: сброс браузерных стилей до прежнего вида иконки.
    border: none;
    background: none;
    color: var(--ac-color-dark-gray);
    cursor: pointer;
  }

  &_clear {
    position: absolute;
    right: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: none;
    color: var(--ac-color-dark-gray);
    cursor: pointer;
  }
}
</style>
